import { useCallback, useRef, useState } from "react";

// --- Web Audio cues (no assets). Silently no-op if audio is unavailable. ---
let _ctx = null;
function ctx() {
  _ctx = _ctx || new (window.AudioContext || window.webkitAudioContext)();
  if (_ctx.state === "suspended") _ctx.resume();
  return _ctx;
}
function tone(freq, dur, { type = "square", vol = 0.06, at = 0 } = {}) {
  try {
    const c = ctx();
    const now = c.currentTime + at;
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, now);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(vol, now + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    osc.connect(gain).connect(c.destination);
    osc.start(now);
    osc.stop(now + dur + 0.02);
  } catch (_) {
    /* audio unavailable */
  }
}

export function playTick() {
  tone(1200, 0.045, { vol: 0.03 });
}
export function playCountBeat(n) {
  tone(320 + (3 - n) * 90, 0.16, { type: "triangle", vol: 0.09 });
}
export function playReveal() {
  tone(660, 0.14, { type: "triangle", vol: 0.09, at: 0 });
  tone(880, 0.14, { type: "triangle", vol: 0.09, at: 0.12 });
  tone(1320, 0.28, { type: "triangle", vol: 0.1, at: 0.24 });
}

// Suspenseful spin with a 3-2-1 countdown.
// - Full motion: fast decoy flicker that slows into the reveal, with sound.
// - Reduced motion (sensory-friendly): a calm, silent countdown, no strobing
//   decoys; the ring + pop are neutralized by CSS. Everyone still gets a moment.
export function useSpin(reducedMotion) {
  const [display, setDisplay] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const timers = useRef([]);
  const flick = useRef(null);

  const clearAll = () => {
    timers.current.forEach((t) => { clearTimeout(t); clearInterval(t); });
    timers.current = [];
    if (flick.current) { clearInterval(flick.current); flick.current = null; }
  };

  const run = useCallback(
    (final, pool) => {
      clearAll();
      if (!pool || pool.length < 2) {
        setSpinning(false);
        setCountdown(null);
        setDisplay(final);
        return;
      }
      const motion = !reducedMotion;
      const rand = () => pool[Math.floor(Math.random() * pool.length)];

      setSpinning(true);
      setCountdown(3);
      setDisplay({ category: final.category, text: motion ? pool[0] : "", spinning: true });
      if (motion) playCountBeat(3);

      const startFlick = (ms) => {
        if (!motion) return;
        if (flick.current) clearInterval(flick.current);
        flick.current = setInterval(
          () => setDisplay({ category: final.category, text: rand(), spinning: true }),
          ms
        );
      };
      startFlick(70);

      const beat = (n, ms, pace) =>
        timers.current.push(setTimeout(() => {
          setCountdown(n);
          if (motion) { playCountBeat(n); playTick(); startFlick(pace); }
        }, ms));

      if (motion) {
        beat(2, 950, 120);
        beat(1, 1900, 220);
        timers.current.push(setTimeout(() => startFlick(360), 2500));
      } else {
        beat(2, 700);
        beat(1, 1400);
      }

      const revealAt = motion ? 2900 : 2100;
      timers.current.push(setTimeout(() => {
        if (flick.current) { clearInterval(flick.current); flick.current = null; }
        setCountdown(null);
        setSpinning(false);
        setDisplay(final);
        if (motion) playReveal();
      }, revealAt));
    },
    [reducedMotion]
  );

  return { display, spinning, countdown, run, setDisplay };
}
